import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle } from 'react-icons/fi'
import { useAuthStore } from '../store'
import api, { authService } from '../services/api'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { useVersion } from '../hooks/useVersion'

const Login = () => {
  const navigate = useNavigate()
  const { login, isAuthenticated } = useAuthStore()
  const version = useVersion()
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [loginResponse, setLoginResponse] = useState(null)
  const [selectedTenant, setSelectedTenant] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  const onSubmit = async (data) => {
    setLoading(true)
    setError('')

    try {
      const response = await authService.login(data)

      if (response.data.success) {
        // Se tiver múltiplos tenants, mostrar seletor
        if (response.data.user.tenants && response.data.user.tenants.length > 1) {
          setLoginResponse(response.data)
          setSelectedTenant(response.data.user.tenant.id)
        } else {
          // Se tiver apenas 1 tenant, ir direto
          login(response.data)
          toast.success('Login realizado com sucesso!')
          navigate('/')
        }
      }
    } catch (err) {
      console.error('Login error:', err)
      const message = err.response?.data?.error || 'Erro ao fazer login'
      setError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleTenantSelect = async () => {
    if (!selectedTenant || !loginResponse) return

    setLoading(true)

    try {
      // Chamar API para trocar de tenant e gerar novo token
      const response = await api.post('/api/auth/switch-tenant', {
        tenantId: parseInt(selectedTenant)
      }, {
        headers: {
          Authorization: `Bearer ${loginResponse.tokens.access}`
        }
      })

      if (response.data.success) {
        // Atualizar login com novos tokens
        const updatedLoginData = {
          ...loginResponse,
          user: {
            ...loginResponse.user,
            tenant: response.data.tenant
          },
          tokens: response.data.tokens
        }

        login(updatedLoginData)
        toast.success(`Conectado à ${response.data.tenant.nome}!`)
        navigate('/')
      }
    } catch (err) {
      console.error('Erro ao trocar de tenant:', err)
      toast.error('Erro ao selecionar empresa')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src="/logo.png"
              alt="AlBaaOne Monitor"
              className="h-20 w-auto dark:hidden"
            />
            <img
              src="/logo-dark.png"
              alt="AlBaaOne Monitor"
              className="h-20 w-auto hidden dark:block"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AlBaaOne Monitor</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">Entre para acessar o dashboard</p>
        </div>

        {/* Card */}
        <div className="card rounded-2xl p-8">
          {/* Tenant Selector (shown after successful login with multiple tenants) */}
          {loginResponse ? (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Selecione a Empresa</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">Você tem acesso a múltiplas empresas. Selecione qual deseja acessar:</p>

              <div className="space-y-4">
                {loginResponse.user.tenants.map((tenant) => (
                  <label
                    key={tenant.id}
                    className={clsx(
                      'flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all bg-white dark:bg-gray-900/30',
                      selectedTenant === tenant.id
                        ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                    )}
                  >
                    <input
                      type="radio"
                      name="tenant"
                      value={tenant.id}
                      checked={selectedTenant === tenant.id}
                      onChange={(e) => setSelectedTenant(parseInt(e.target.value))}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="ml-3">
                      <p className="font-medium text-gray-900 dark:text-white">{tenant.nome}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Plano: {tenant.plan_type === 3 ? 'Completo' : tenant.plan_type === 2 ? 'Intermediário' : 'Básico'}</p>
                    </div>
                  </label>
                ))}
              </div>

              <button
                type="button"
                onClick={handleTenantSelect}
                disabled={!selectedTenant}
                className="btn-primary w-full mt-6"
              >
                Continuar
              </button>

              <button
                type="button"
                onClick={() => {
                  setLoginResponse(null)
                  setSelectedTenant(null)
                }}
                className="btn-secondary w-full mt-3"
              >
                Voltar
              </button>
            </div>
          ) : (
            <>
              {/* Error Alert */}
              {error && (
                <div className="mb-6 p-4 bg-danger-50 border border-danger-200 dark:bg-danger-900/20 dark:border-danger-800 rounded-lg flex items-start gap-3">
                  <FiAlertCircle className="text-danger-600 dark:text-danger-300 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-danger-700 dark:text-danger-200">{error}</p>
                  </div>
                </div>
              )}
              
              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Email */}
                <div>
                  <label className="label">Email</label>
                  <div className="relative">
                    <input
                      type="email"
                      className={clsx(
                        'input pl-10',
                        errors.email && 'input-error'
                      )}
                      placeholder="seu@email.com"
                      {...register('email', {
                        required: 'Email é obrigatório',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email inválido'
                        }
                      })}
                    />
                    <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  </div>
                  {errors.email && (
                    <p className="text-danger-600 text-xs mt-1">{errors.email.message}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="label">Senha</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className={clsx(
                        'input pl-10 pr-10',
                        errors.password && 'input-error'
                      )}
                      placeholder="••••••••"
                      {...register('password', {
                        required: 'Senha é obrigatória',
                        minLength: {
                          value: 6,
                          message: 'Senha deve ter no mínimo 6 caracteres'
                        }
                      })}
                    />
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-danger-600 text-xs mt-1">{errors.password.message}</p>
                  )}
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      {...register('remember')}
                    />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">Lembrar de mim</span>
                  </label>
                  <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
                    Esqueceu a senha?
                  </Link>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={clsx(
                    'w-full btn-primary',
                    loading && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="spinner"></div>
                      <span className="ml-2">Entrando...</span>
                    </div>
                  ) : (
                    'Entrar'
                  )}
                </button>
              </form>
            </>
          )}

          {/* Divider */}
          {!loginResponse && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-center text-gray-600 dark:text-gray-300">
                Não tem uma conta?{' '}
                <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
                  Cadastre-se
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8 space-y-1">
          © 2025 AlBaaOne Soluções. Todos os direitos reservados.
          {version?.label && (
            <div className="text-xs text-gray-400">
              Versão {version.label}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Login
